import { wrapAsArray } from "@cervval.js/common/src/tools/collection.array.tools";
import { anyReallyAny } from "@cervval.js/common/tools";

export type DayAgendaSelectedData<ItemSelectedType> = { item: ItemSelectedType, htmlItem: HTMLElement, data?: anyReallyAny };

/** Handle Timeline items selection */
export class DayAgendaSelectionManager<ItemSelectedType> {

    constructor(private _param: { getItemUid: (item: ItemSelectedType) => string }) {
        this.getItemUid = this._param.getItemUid;
    }

    private _selectedData: Map<string, DayAgendaSelectedData<ItemSelectedType>> = new Map<string, DayAgendaSelectedData<ItemSelectedType>>();

    /** Return the selected data */
    public getSelectedData(): Map<string, DayAgendaSelectedData<ItemSelectedType>> {
        return this._selectedData;
    }

    /** Set the selected data */
    public setSelectedData(newSelectedData: DayAgendaSelectedData<ItemSelectedType>[], options?: { removeFromSelectionIfAlreadySelected?: boolean, event?: MouseEvent }): void {
        if (!options?.event?.ctrlKey) {
            this.resetSelectedData();
        }
        this.addToSelectedData(newSelectedData, options);
    }

    /** Return the map of selected data converted into an array */
    public getSelectedDataArray(): DayAgendaSelectedData<ItemSelectedType>[] {
        return Array.from(this._selectedData, ([k, v]) => v);
    }

    /**
     * Reset the selected data to an empty array 
     * @returns Array of removed (previously selected) data
     */
    public resetSelectedData(): DayAgendaSelectedData<ItemSelectedType>[] {
        const removedData = this.getSelectedDataArray();
        this.onRemove(removedData);
        this.onSelectionReset();
        this._selectedData.clear();
        return removedData;
    }

    /** Add an item to the selected data */
    public addToSelectedData(additionnalData: DayAgendaSelectedData<ItemSelectedType> | DayAgendaSelectedData<ItemSelectedType>[], options?: { removeFromSelectionIfAlreadySelected?: boolean, doNotDispatchOnAddEvent?: boolean }): void {
        const additionnalSelection = wrapAsArray(additionnalData);

        const dataToAdd: DayAgendaSelectedData<ItemSelectedType> | DayAgendaSelectedData<ItemSelectedType>[] = [];
        for (const ad of additionnalSelection) {
            const adItemUid = this.getItemUid(ad.item);
            const selectedItem = this._selectedData.get(adItemUid);
            if (selectedItem) {
                if (options?.removeFromSelectionIfAlreadySelected) {
                    this.removeItemFromSelectedData(ad.item);
                }
            } else {
                this._selectedData.set(adItemUid, ad);
                dataToAdd.push(ad);
            }
        }
        if (!options?.doNotDispatchOnAddEvent) {
            this.onAdd(dataToAdd);
        }
    }

    /** Remove an item from the selected data using the data comparator given to the constructor */
    public removeItemFromSelectedData(itemToRemove: ItemSelectedType): void {
        const itemUid = this.getItemUid(itemToRemove);
        const selectionToRemove = this._selectedData.get(itemUid);
        if (selectionToRemove) {
            this.onRemove([selectionToRemove]);
            this._selectedData.delete(itemUid);
        }
    }


    /** Method called on data adding, By default does nothing, can be customised */
    public onAdd(selectedData: DayAgendaSelectedData<ItemSelectedType>[]): void {

    }

    /** Method called on data removing, By default does nothing, can be customised */
    public onRemove(selectedData: DayAgendaSelectedData<ItemSelectedType>[]): void {

    }

    /** Method called on selection reset. By default does nothing, can be customised */
    public onSelectionReset(): void {

    }

    /** Getter to be defined, used to retreive an item uid. Uid is used in the selected data map. */
    public getItemUid(item: ItemSelectedType): string {
        throw new Error("Method getItemUid of SelectionManager not implemented");
    }
}